/**
 * Contract Tab Component
 * Shows employment contract status and signing
 */
import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  FileText,
  Signature,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Download,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContractTab({ profile, contract }) {
  const [downloading, setDownloading] = useState(false);

  const handleViewContract = async () => {
    if (!contract?.contract_id) {
      toast.error("Contract not found");
      return;
    }
    
    setDownloading(true);
    try {
      const token = localStorage.getItem("scaffoldx_token");
      const response = await fetch(
        `${API}/employment-contracts/${contract.contract_id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        // Clone response before reading to avoid "body stream already read" error
        const errorText = await response.text();
        let errorMessage = "Failed to load contract";
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.message || "Failed to load contract");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!contract?.contract_id) {
      toast.error("Contract not found");
      return;
    }
    
    setDownloading(true);
    try {
      const token = localStorage.getItem("scaffoldx_token");
      const response = await fetch(
        `${API}/employment-contracts/${contract.contract_id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to download contract";
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employment_contract_${profile.worker_code || 'worker'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Contract downloaded!");
    } catch (error) {
      toast.error(error.message || "Failed to download contract");
    } finally {
      setDownloading(false);
    }
  };

  // Determine contract status
  const isFullySigned = contract?.status === "fully_signed";
  const isWorkerSigned = contract?.worker_signed || profile.contract_signed;
  const isHrSigned = contract?.hr_signed;

  return (
    <div className="space-y-6">
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader>
          <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
            <Signature className="w-5 h-5 text-[#23F7DD]" />
            Employment Contract
            {isFullySigned && (
              <CheckCircle2 className="w-5 h-5 text-green-400 ml-2" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isWorkerSigned ? (
            <div className="space-y-4">
              {/* Worker signature status */}
              <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                  <span className="text-green-400 font-medium">Your Signature</span>
                </div>
                <p className="text-sm text-[#94A3B8]">
                  Signed by: {contract?.worker_signature || profile.contract_signature || "Worker"}<br/>
                  Date: {(contract?.worker_signed_at || profile.contract_signed_at) 
                    ? new Date(contract?.worker_signed_at || profile.contract_signed_at).toLocaleString() 
                    : "N/A"}
                </p>
              </div>
              
              {/* HR signature status */}
              {isHrSigned ? (
                <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                    <span className="text-green-400 font-medium">HR Signature (Employer)</span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">
                    Signed by: {contract?.hr_signed_by_name || "HR Manager"}<br/>
                    Date: {contract?.hr_signed_at 
                      ? new Date(contract.hr_signed_at).toLocaleString() 
                      : "N/A"}
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-6 h-6 text-yellow-400" />
                    <span className="text-yellow-400 font-medium">Waiting for HR Signature</span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">
                    Your contract is pending HR approval. You will be notified once it&apos;s signed.
                  </p>
                </div>
              )}
              
              {/* View/Download buttons - only when fully signed */}
              {isFullySigned && (
                <div className="flex gap-3">
                  <Button 
                    onClick={handleViewContract}
                    disabled={downloading}
                    variant="outline" 
                    className="flex-1 border-[#23F7DD]/50 text-[#23F7DD] hover:bg-[#23F7DD]/10"
                  >
                    {downloading ? (
                      <span className="w-4 h-4 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin mr-2"></span>
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    View Contract
                  </Button>
                  <Button 
                    onClick={handleDownloadContract}
                    disabled={downloading}
                    className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                  >
                    {downloading ? (
                      <span className="w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full animate-spin mr-2"></span>
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download PDF
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  <div>
                    <p className="text-yellow-400 font-medium">Contract Not Signed</p>
                    <p className="text-sm text-[#94A3B8]">
                      You must sign the employment contract before you can apply for jobs.
                    </p>
                  </div>
                </div>
              </div>
              
              <a href="/worker/employment-contract" className="block">
                <Button className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold py-6">
                  <Signature className="w-5 h-5 mr-2" />
                  View & Sign Contract
                </Button>
              </a>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
